import { Guid } from 'guid-typescript';
import { AppSettings } from 'src/app/app.settings';
import { Settings } from './../app.settings.model';
import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from './../../environments/environment';
import { SessionUser } from './session.user';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    private currentUserSubject: BehaviorSubject<SessionUser>;
    public currentUser: Observable<SessionUser>;
    public currentUserSubscription: Observable<Guid>;
    public currentUserService: Observable<Guid>;
    public currentUserRole: Observable<string>;
    public settings: Settings;

    constructor(private http: HttpClient,
        public appSettings: AppSettings,
        private router: Router) {
        this.settings = this.appSettings.settings;
        // If you are confident that the localStorage.getItem() 
        // call can never return null you can use the non-null assertion operator to tell typescript that you know what you are doing:
        //this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('currentUser') || '{}'));
        this.currentUserSubject = new BehaviorSubject<SessionUser>(JSON.parse(localStorage.getItem('sessionUser')));
        this.currentUser = this.currentUserSubject.asObservable();
    }

    public get currentUserValue(): SessionUser {
        if (this.currentUserSubject.value != null)
            return this.currentUserSubject.value;
        else
            return new SessionUser;
    }

    // login(username: string, password: string) {
    //     return this.http.post<SessionUser>(`${environment.apiUrl}/Account/Token`, { username, password }).pipe(map(user => {
    //         // login successful if there's a jwt token in the response            
    //         if (user && user.token) {
    //             // store user details and jwt token in local storage to keep user logged in between page refreshes
    //             localStorage.setItem('sessionUser', JSON.stringify(user));
    //             localStorage.setItem('currentUserSubscription', user.services[0].service.subscriptionId.toString());
    //             let currentUserService = localStorage.currentUserService || user.services[0].service.id;
    //             if (user.services.findIndex(p => p.service.id == currentUserService) == -1) {
    //                 currentUserService = user.services[0].service.id;
    //             }
    //             localStorage.setItem('currentUserService', currentUserService);

    //             var item = user.services.filter(p => p.service.id == currentUserService)[0];
    //             localStorage.setItem('currentProfileId', item.profileId.toString());

    //             var roles = user.roles.filter(p => p.serviceId == currentUserService);
    //             let currentUserRole = localStorage.currentUserRole || roles[0].name;

    //             if (roles.findIndex(p => p.name == currentUserRole) == -1) {
    //                 currentUserRole = roles[0].name;
    //             }

    //             localStorage.setItem('currentUserRole', currentUserRole);
    //             let currentUserRoleId = JSON.parse(localStorage.getItem('sessionUser')).roles.filter(item => item.name == currentUserRole)[0].id;
    //             localStorage.setItem('currentUserRoleId', currentUserRoleId);

    //             this.currentUserSubject.next(user);
    //         }
    //         return user;
    //     }));
    // }

    // ─── Login ────────────────────────────────────────────────────────────────
    login(username: string, password: string): Observable<SessionUser> {
        return this.http
            .post<SessionUser>(`${environment.apiUrl}/Account/Token`, { username, password })
            .pipe(
                map(user => {
                    if (user && user.token) {
                        this._persistSession(user);
                    }
                    return user;
                })
            );
    }

    // ─── Silent refresh (call on app init / 401) ──────────────────────────────
    refreshToken(): Observable<SessionUser> {
        const storedRefreshToken = localStorage.getItem('refreshToken');
        if (!storedRefreshToken) {
            this.logout();
            return throwError(() => new Error('No refresh token available'));
        }
        return this.http.post<SessionUser>(`${environment.apiUrl}/Account/RefreshToken`, {
            refreshToken: storedRefreshToken
        }).pipe(
            map(user => {
                if (user && user.token) {
                    this._persistSession(user);
                }
                return user;
            })
        );
    }

    // ─── Logout ───────────────────────────────────────────────────────────────
    logout(): void {
        this.settings.sidenavIsOpened = true;

        const storedRefreshToken = localStorage.getItem('refreshToken');
        if (storedRefreshToken) {
            this.http
                .post(`${environment.apiUrl}/Account/Logout`, { refreshToken: storedRefreshToken })
                .subscribe({ error: () => { } });
        }

        const returnUrl = localStorage.getItem('returnUrl') || localStorage.returnUrl;
        localStorage.clear();
        this.currentUserSubject.next(null);

        if (returnUrl) {
            window.location.href = returnUrl;
        } else {
            this.router.navigate(['/login']);
        }
    }

    // ─── Helpers ──────────────────────────────────────────────────────────────

    /** Persist session user + all derived localStorage keys. */
    private _persistSession(user: SessionUser): void {
        // Store session and refresh token
        localStorage.setItem('sessionUser', JSON.stringify(user));
        if (user.refreshToken) {
            localStorage.setItem('refreshToken', user.refreshToken);
        }

        // Service
        if (!user.services?.length || !user.profiles?.length || !user.roles?.length) {
            this.currentUserSubject.next(user);
            return;
        }
        let currentUserService =
            localStorage.getItem('currentUserService') || user.services[0].service.id.toString();

        if (user.services.findIndex(p => p.service.id.toString() === currentUserService) === -1)
            currentUserService = user.services[0].service.id.toString();
        localStorage.setItem('currentUserService', currentUserService);
        localStorage.setItem('currentUserSubscription', user.services[0].service.subscriptionId.toString());

        // Profile
        const profilesForService = user.profiles.filter(
            p => p.serviceId.toString() === currentUserService
        );
        let currentProfileId = localStorage.getItem('currentProfileId');
        if (!currentProfileId ||
            profilesForService.findIndex(p => p.profileId.toString() === currentProfileId) === -1
        ) {
            currentProfileId = profilesForService[0]?.profileId?.toString();
        }
        localStorage.setItem('currentProfileId', currentProfileId ?? '');

        // Role
        const rolesForProfile = user.roles.filter(
            r => r.serviceId.toString() === currentUserService &&
                r.profileId.toString() === currentProfileId
        );
        let currentUserRole = localStorage.getItem('currentUserRole');
        if (!currentUserRole || rolesForProfile.findIndex(r => r.name === currentUserRole) === -1)
            currentUserRole = rolesForProfile[0]?.name;
        localStorage.setItem('currentUserRole', currentUserRole);

        const selectedRole = rolesForProfile.find(r => r.name === currentUserRole);
        if (selectedRole)
            localStorage.setItem('currentUserRoleId', selectedRole.id.toString());

        this.currentUserSubject.next(user);
    }

    // ─── Other existing methods — unchanged ───────────────────────────────────

    handlePermission(screenName: string): Observable<boolean> {
        return this.http.post<boolean>(
            `${environment.apiUrl}/RolePermission/HandlePermission`,
            { screenName }
        );
    }
}
